// Step changes on the screens (docs/step-changes-brief.md, 2026-10-24): the one record (ug.step_change) read for the Settings nav's
// count, the stage headers, the ghost rows and proposed values on the steps tables, and the who-when-Undo of a direct write.
// Every write goes through the database's three functions; the screens re-read what they touch.

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type StepChangeContext, type StepChangeRow, type StepChangeTable } from '@zerogap/engine';
import { refresh, rpc, useRows, type Row } from './api';
import type { Pipeline } from './pipeline';

export const STEP_CHANGE_TABLES = ['pipeline_step', 'workflow_step', 'pipeline_stage', 'step_change', 'standup_card', 'audit', 'opportunity', 'activity', 'setting'];

/** Every step change, newest first. */
export function useStepChanges(): { rows: StepChangeRow[]; loading: boolean } {
  const q = useRows('step_change', { order: ['-suggested_at'] });
  return { rows: q.rows as unknown as StepChangeRow[], loading: q.loading };
}

/** The tab a change belongs to: Pipeline steps for a pipeline step or a stage, Workflow steps for a job step. */
export const changeView = (c: Pick<StepChangeRow, 'target_table'>): 'pipeline' | 'workflow' => (c.target_table === 'workflow_step' ? 'workflow' : 'pipeline');

/** The suggestions waiting on one tab. */
export function waitingOn(rows: StepChangeRow[], view: 'pipeline' | 'workflow'): StepChangeRow[] {
  return rows.filter((c) => c.status === 'waiting' && changeView(c) === view);
}

/** Files one change: at the login's level it lands, else it waits. The database answers with the record's id and words. */
export async function fileStepChange(table: StepChangeTable, kind: StepChangeRow['kind'], target: string | null, proposed: Record<string, unknown>, reason: string | null, stage: string | null = null) {
  const r = await rpc<{ status: string; change_id: string; words: string }>('step_change_file', { p_table: table, p_kind: kind, p_target: target, p_proposed: proposed, p_reason: reason, p_stage: stage });
  refresh(...STEP_CHANGE_TABLES);
  return r;
}

/** Approve (as filed, or with the edited values laid over the proposal) or send back with a note. */
export async function decideStepChange(id: string, approve: boolean, values: Record<string, unknown> | null, note: string | null) {
  const r = await rpc<{ status: string; change_id: string; words: string }>('step_change_decide', { p_change: id, p_approve: approve, p_values: values, p_note: note });
  refresh(...STEP_CHANGE_TABLES);
  return r;
}

/** Undo on the row: puts back what the change wrote (a retire's leads with it; an undone add retires the step it made). */
export async function undoStepChange(id: string) {
  const r = await rpc<{ undone: boolean; put_back: number; left_as_moved_on: number }>('step_change_undo', { p_change: id });
  refresh(...STEP_CHANGE_TABLES);
  return r;
}

/** The names the words need, from the pipeline view model and the job steps. */
export function changeContext(p: Pipeline, wsteps: Row[]): StepChangeContext {
  return {
    stepName: (table, id) => (id ? ((table === 'pipeline_step' ? p.stepById(id)?.name : (wsteps.find((s) => s.id === id)?.name as string | undefined)) ?? null) : null),
    stageName: (id) => p.stageById(id)?.name ?? null,
    seatName: (id) => (p.seats.find((s) => s.id === id)?.name as string | undefined) ?? null,
    conditionLabel: (k) => p.conditions.find((c) => c.key === k)?.label ?? null,
    eventLabel: (k) => p.events.find((e) => e.key === k)?.label ?? null,
    leadsOn: (stepId) => p.open.filter((l) => l.step?.id === stepId || l.opp.current_step_id === stepId).length,
    tabName: (t) => (t ? (wsteps.find((s) => s.tab === t)?.name as string | undefined) ?? null : null),
  };
}

/** The row a change sits on, as it stands now, with a stage's quiet days laid on (what stale and standing compare). */
export function rowNow(c: StepChangeRow, p: Pipeline, wsteps: Row[]): Record<string, unknown> | null {
  if (!c.target_id) return null;
  if (c.target_table === 'pipeline_step') return (p.stepById(c.target_id) as unknown as Record<string, unknown>) ?? null;
  if (c.target_table === 'workflow_step') return (wsteps.find((s) => s.id === c.target_id) as Record<string, unknown> | undefined) ?? null;
  const st = p.stageById(c.target_id);
  return st ? { ...(st as unknown as Record<string, unknown>), quiet_days: p.quietSetting.by_stage?.[st.key] ?? null } : null;
}

/** ?change=<id> (the Standup card's link): the stage unfolds, the row scrolls into view and is outlined for two seconds. */
export function useChangeFromAddress(rows: StepChangeRow[], open: (c: StepChangeRow) => void, light: (id: string | null) => void) {
  const [params] = useSearchParams();
  const want = params.get('change');
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (!want || done.current === want) return;
    const c = rows.find((x) => x.id === want);
    if (!c) return;
    done.current = want;
    open(c);
    window.setTimeout(() => {
      const row = document.querySelector(`[data-change="${c.id}"]`) ?? (c.target_id ? document.querySelector(`[data-step="${c.target_id}"]`) : null);
      row?.scrollIntoView({ block: 'center' });
      light(c.id);
      window.setTimeout(() => light(null), 2000);
    }, 250);
  }, [want, rows, open, light]);
}
